// ---------- English dictionary ----------
// Mirrors fr.ts key for key — typed as Record<I18nKey, string> below so a missing or extra key
// is a COMPILE ERROR, never a French string (or nothing at all) silently reaching production.
// See fr.ts for the conventions this file follows (flat keys, `{{var}}` placeholders carrying
// whole sentences, the three shared/ registries handled separately in ./index.ts).
import type { I18nKey } from './fr';

const en: Record<I18nKey, string> = {
  // ---------- brand (index.html, p.html, splash.ts) ----------
  'brand.name': 'Codigrid',

  // ---------- index.html <head> ----------
  'app.title': 'Codigrid — pattern grid',
  'app.description': 'A logic colouring game: a picture becomes a grid of coded symbols.',

  // ---------- shared across dialogs ----------
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.back': 'Back',

  // ---------- home.ts / index.html #home ----------
  'home.filter.doing': 'In progress',
  'home.filter.done': 'Finished',
  'home.tab.feed': 'My feed',
  'home.tab.daily': 'Daily',
  'home.tab.library': 'Library',
  'home.tab.settings': 'Settings',
  'home.tab.create': 'Create',
  'home.tab.new': 'New',
  'home.category.celebrite': 'Celebrities',
  'home.category.personnes': 'Portraits',
  'home.category.monuments': 'Landmarks',
  'home.category.scenes': 'Everyday life',
  'home.category.contes': 'Tales and legends',
  'home.category.saisonnier': 'Seasonal',
  'home.category.paysage': 'Landscape',
  'home.category.animaux': 'Animals',
  'home.category.vehicules': 'Vehicles',
  'home.category.sport': 'Sport',
  'home.category.art': 'Art',
  'home.category.divers': 'Misc',
  'home.category.other': 'Other',
  'home.facet.category.title': 'Category',
  'home.facet.category.all': 'All',
  'home.facet.difficulty.title': 'Difficulty',
  'home.facet.difficulty.all': 'All',
  'home.facet.alphabet.title': 'Alphabet',
  'home.facet.alphabet.all': 'All',
  // "cells" stays invariant on purpose — see the note on this key in fr.ts: no real puzzle is
  // ever a single cell (the smallest grid is 4×4), so the singular case never actually shows.
  'home.card.tooltip': '{{w}} × {{h}} cells · {{diff}}',
  'home.card.new': 'New',
  // ---------- the duration shown on a card (home.ts, shared/effort.ts) ----------
  'home.card.eta': '≈ {{d}}',
  'home.card.etaLong': 'about {{d}} in total',
  'home.card.leftLong': 'about {{d}} left',
  'time.unit.min': 'min',
  'time.unit.h': 'h',
  'time.min': '{{n}} min',
  'time.h': '{{n}} h',
  'time.hmin': '{{h}} h {{m}}',
  'time.hplus': '{{n}} h+',
  'home.empty.noMatch': 'No puzzle matches these filters yet.',
  'home.empty.resetFilters': 'Reset filters',
  'home.empty.libraryDone': "You've touched every puzzle available. Come back — more are coming.",
  'home.empty.libraryDonePacks':
    "You've started every puzzle you have. New packs are waiting for you.",
  'home.empty.seePacks': 'See the packs',
  'home.empty.puzzleGone': 'This puzzle is unavailable.',
  'home.empty.retry': 'Retry',
  'home.empty.noneDoing': 'No puzzle in progress. Pick one from the library.',
  'home.empty.openLibrary': 'Open the library',
  'home.empty.noneDone': "Nothing finished yet. It'll come.",
  'home.empty.noPuzzles': 'No puzzle available yet.',
  'home.empty.serverError': "Couldn't reach the server.",
  'home.loading': 'Loading…',
  // ---------- the feed's rating card (home.ts::rateCard, rules in review.ts) ----------
  'home.rate.1.title': 'You know Codigrid well by now',
  'home.rate.1.body':
    'Your review on the Play Store helps other colouring fans decide whether the game is right for them.',
  'home.rate.1.cta': 'Share my review',
  'home.rate.2.title': 'Having a good time with Codigrid?',
  'home.rate.2.body':
    'Say so in 10 seconds on the Play Store — for a small game, it really makes all the difference.',
  'home.rate.2.cta': 'Rate Codigrid',
  'home.rate.3.title': 'Fancy some new puzzles?',
  'home.rate.3.body': 'Every review on the Play Store counts: it’s what lets me keep making them.',
  'home.rate.3.cta': 'Leave a review',
  'home.progression.open': 'Progression',
  'home.packsBanner.sub': '{{n}} packs to unlock',
  'home.packsBanner.one': '1 pack to unlock',
  'home.packsBanner.cta': 'Explore',

  // ---------- creations: home.ts (card, filter, cards) and creer.ts (the tool) ----------
  'home.create.title': 'Create my puzzle',
  'home.create.sub': 'From one of your photos',
  'home.facet.mine': 'My creations',
  'home.pack.base': 'Getting started',
  'home.card.mine': 'My creation',
  'home.empty.noCreations': "You haven't created a puzzle yet. Your creations will show up here.",
  'home.creation.delete': 'Delete this creation',
  'home.creation.deleteConfirm':
    'Delete “{{title}}”? The puzzle and your progress on it will be lost.',
  'creer.step': '{{n}} / {{total}}',
  'creer.next': 'Continue',
  'creer.busy': 'Preparing the sheet…',
  'creer.photo.title': 'Your photo',
  'creer.photo.camera': 'Take a photo',
  'creer.photo.gallery': 'My gallery',
  'creer.photo.tipsTitle': 'The photos that make the best puzzles',
  'creer.photo.tip1': 'A subject that fills the frame',
  'creer.photo.tip1Sub': 'A face, an animal, a silhouette, seen up close.',
  'creer.photo.tip2': 'Contrast',
  'creer.photo.tip2Sub': 'Well-marked lights and darks: they are what draws the picture.',
  'creer.photo.tip3': 'A sharp photo, in full light',
  'creer.photo.tip3Sub': 'Daylight, no backlight, no blur.',
  'creer.photo.tip4': 'No tiny details',
  'creer.photo.tip4Sub': 'Text, a crowd or a distant landscape get lost in the cells.',
  'creer.photo.cutTitle': 'A portrait? Remove the background first',
  'creer.photo.cutText':
    'With a tool such as {{link}}, then pick the image in My gallery: the background turns into paper.',
  'creer.photo.private':
    '<b>Your photo is never sent.</b> The grid is made on your phone, and only you can see the puzzle.',
  'creer.photo.error': "This image can't be read. Try another photo.",
  'creer.crop.title': 'Frame it',
  'creer.crop.hint': 'Pinch to zoom, drag to move',
  'creer.crop.photo': 'Photo',
  'creer.crop.square': 'Square',
  'creer.crop.portrait': 'Portrait',
  'creer.crop.landscape': 'Landscape',
  'creer.crop.rotate': 'Rotate a quarter turn',
  'creer.adjust.title': 'Your sheet',
  'creer.tool.tech': 'Technique',
  'creer.tool.detail': 'Detail',
  'creer.tool.light': 'Light',
  'creer.light.normal': 'Normal',
  'creer.light.clear': 'Bright',
  'creer.light.notch': 'Light {{n}} of {{total}}',
  'creer.tool.contrast': 'Contrast',
  'creer.contrast.soft': 'Natural',
  'creer.contrast.strong': 'Bold',
  'creer.contrast.notch': 'Contrast {{n}} of {{total}}',
  'creer.zoomReset': 'Show the whole preview',
  'creer.detail.cells': 'Number of cells',
  'creer.detail.readout': '{{w}} × {{h}} · {{n}} cells',
  'creer.detail.notch': '{{n}} cells per side',
  'creer.detail.less': 'Quick to paint',
  'creer.detail.more': 'Lots of detail',
  'creer.ready.title': 'Summary',
  'creer.ready.name': 'Its name',
  'creer.ready.placeholder': 'My puzzle',
  'creer.ready.photo':
    '<b>Your photo stays on your phone.</b> It is never sent, and neither is the puzzle made from it.',
  'creer.ready.private':
    '<b>This puzzle is yours alone, on this phone.</b> It isn’t kept anywhere else: if you uninstall the game or change phones, it won’t follow you.',
  'creer.ready.go': 'Save the puzzle',
  'creer.ready.saving': 'Saving…',
  'creer.ready.storeFailed':
    'The puzzle couldn’t be saved on this phone. Free up some space and try again.',
  'creer.ready.tooMany':
    'You already have {{n}} creations, the maximum. Delete one from “My creations” to make a new one.',
  'creer.ready.full': 'Creating puzzles is unavailable for the moment. Try again later.',
  'creer.ready.freeLeftMany': '{{n}} more free creations, no ads.',
  'creer.ready.freeLeftOne': '1 more free creation, no ads.',
  'creer.ready.adNote':
    'Your first creation was free. This one saves after a short ad — that’s what keeps the game going.',
  'creer.ready.goAd': 'Watch an ad and save',
  'creer.ready.adSkipped':
    'Ad interrupted: your creation isn’t saved yet. Try again whenever you like.',
  'creer.ready.adUnavailable': 'No ad available right now. Try again in a moment.',

  // ---------- daily.ts / index.html #dailyPage (the puzzle of the day) ----------
  'daily.month.prev': 'Previous month',
  'daily.month.next': 'Next month',
  'daily.state.todo': 'To paint',
  'daily.state.doing': 'In progress, {{pct}}%',
  'daily.state.done': 'Finished',
  'daily.cell.aria': '{{date}} — {{state}}',
  'daily.empty.stock': 'No puzzle today. Come back tomorrow.',
  'daily.empty.update': 'This puzzle needs a newer version of the app.',
  'daily.today.kicker': 'Today’s sheet',
  'daily.today.unknown': 'Nobody knows what it is yet',
  'daily.today.paint': 'Paint it',
  'daily.today.resume': 'Carry on',
  'daily.today.see': 'See it again',

  // ---------- packs.ts / index.html #packs (unlockable puzzle packs) ----------
  'packs.title': 'Available packs',
  'packs.carousel': 'carousel',
  'packs.loading': 'Loading…',
  'packs.empty': 'Nothing to unlock yet. Come back — more is coming.',
  'packs.puzzlesLabel.one': 'puzzle',
  'packs.puzzlesLabel.other': 'puzzles',
  'packs.selection': 'Selection',
  'packs.unlock': 'Unlock',
  'packs.watchAd': 'Watch an ad',
  // ---------- les arguments d'un pack (src/packArgument.ts) ----------
  'packs.arg.debutant': 'Great to start with',
  'packs.arg.portraits': 'Faces',
  'packs.arg.patience': 'Take your time',
  'packs.arg.nouveau': 'Brand new',
  'packs.arg.costaud': 'Big grids',
  'packs.arg.express': 'Short sessions',
  'packs.arg.mono': 'One technique',
  'packs.arg.varie': 'Mixed techniques',
  'packs.arg.couleur': 'In colour',
  'packs.unlocking': 'Unlocking…',
  // Under the Unlock button: reassures there's no money involved — cf. src/packs.ts,
  // unlockNote(). '<b>' is intentional markup, same precedent as home.intro further down.
  'packs.adNote': '<b>Free</b> · about 30s',
  'packs.freeNote': '<b>Free</b>, one tap',
  'packs.adLoading': 'Loading the ad…',
  'packs.adUnavailable': 'No ad available — try again.',
  'packs.adSkipped': 'Ad not finished.',
  'packs.adFailed': 'Failed — try again.',
  'packs.reveal.title.one': '{{n}} puzzle unlocked!',
  'packs.reveal.title.other': '{{n}} puzzles unlocked!',
  'packs.reveal.sub': '"{{title}}" joined your Library',
  'packs.reveal.continue': 'Continue',
  'packs.feature.coeur': 'Our pick',
  'packs.feature.saison': 'In season',
  'packs.feature.nouveau': 'New',
  'packs.feature.evenement': 'Special event',
  'packs.feature.joueurs': 'Players’ favourite',
  'packs.feature.populaire': 'Most unlocked',
  'packs.volume': '{{title}} No. {{n}}',
  'packs.intro': '{{n}} puzzles to discover',
  'packs.shelf.n': '{{n}} packs',
  'packs.unlocks': 'Unlocked by {{n}} players',
  'packs.fact.time': 'of colouring',
  'packs.fact.votes': '{{n}} ratings',
  'packs.fact.unrated': 'not rated yet',
  'packs.inside': 'What’s inside',
  'packs.inside.hint': 'Each subject reveals itself as you colour.',
  'packs.sheet.puzzle': 'Puzzle {{n}}',
  // Ribbon on the "Éclat" card (single-technique pack, cf. src/packs.ts::renderList()).
  // Generic per-family names (src/techniqueIcon.ts::techniqueFamilyLabel()) — distinct from
  // alphabetLabel()'s precise names: used when several alphabetId of the same family are
  // grouped under one illustration (the "Vitrine" card's swatches, the "Éclat" card's subtitle
  // when a pack mixes several alphabetId of the same family).
  'technique.family.motifs': 'Patterns',
  'technique.family.hachures': 'Hatching',
  'technique.family.ascii': 'ASCII',
  'technique.family.pointille': 'Stippling',
  'technique.family.des': 'Dice',
  'technique.family.briques': 'Bricks',
  'technique.family.peinture': 'Paint',
  'technique.family.croix': 'Embroidery',

  // ---------- progression.ts / index.html #progression (badges + Ensemble) ----------
  'progression.title': 'Progression',
  'progression.tier.bronze': 'Bronze',
  'progression.tier.argent': 'Silver',
  'progression.tier.or': 'Gold',
  'progression.tier.platine': 'Platinum',
  'progression.loading': 'Loading…',
  'progression.needAccount': 'Finish a puzzle to see what other players are painting.',
  'progression.loadFailed': "Couldn't reach the server.",
  // L’écran « Moi » et ses badges (src/profile.ts) — les règles, seuils compris, viennent du serveur.
  'progression.tab.me': 'Me',
  // L’onglet « Ensemble » (src/together.ts) et la rareté des badges — ce qui a remplacé la ligue.
  'progression.tab.together': 'Together',
  'me.rarity': '{{pct}}% of players',
  'me.rarity.rare': 'Rare · {{pct}}% of players',
  'me.rarity.locked': 'Already earned by {{pct}}% of players',
  'together.eyebrow': 'This week · since Monday',
  'together.sheets.one': 'puzzle finished',
  'together.sheets.other': 'puzzles finished',
  'together.players.one': 'by {{n}} player',
  'together.players.other': 'by {{n}} players',
  'together.stats': 'The week in numbers',
  'together.trend.eyebrow': 'Trend',
  'together.trend.up': 'compared with last week',
  'together.trend.same': 'about the same as last week',
  'together.trend.calm': 'a little quieter than last week',
  'together.trend.ctx': '{{n}} puzzles, against {{last}} at the same time last week.',
  'together.peak.eyebrow': 'Peak time',
  'together.peak.what': 'when the most colouring happens',
  'together.crossed.eyebrow': 'Badges',
  'together.crossed.one': 'tier reached since Monday',
  'together.crossed.other': 'tiers reached since Monday',
  'together.joined.eyebrow': 'Welcome',
  'together.joined.one': 'new player this week',
  'together.joined.other': 'new players this week',
  'together.joined.ctx': 'Same time last week: {{last}}.',
  'together.empty': 'Nobody has finished a puzzle yet this week. The first one might be yours.',
  'together.mine.title': 'Your share this week.',
  'together.mine.zero': 'No puzzle yet this week. No rush.',
  'together.mine.one': '1 puzzle finished out of {{total}}.',
  'together.mine.other': '{{n}} puzzles finished out of {{total}}.',
  'together.daily.one': "{{n}} player has already painted today's puzzle.",
  'together.daily.other': "{{n}} players have already painted today's puzzle.",
  'together.daily.with': "Including you, {{n}} players have painted today's puzzle.",
  'together.daily.go': 'Paint it',
  'progression.tier.prisme': 'Prism',
  'me.eyebrow': 'Your nickname',
  'me.edit': 'Change nickname',
  'me.choose': 'Pick a nickname',
  'me.since': 'Playing since {{date}}',
  'me.days.one': '{{n}} day',
  'me.days.other': '{{n}} days',
  'me.numbers': 'Your numbers',
  'me.cells.eyebrow': 'Cells painted',
  'me.cells.what': 'since you joined',
  'me.fav.eyebrow': 'Your style',
  'me.fav.cat': 'Favourite category: {{cat}}',
  'me.biggest.eyebrow': 'Your biggest puzzle',
  'me.biggest.cells': '{{n}} cells painted',
  'me.biggest.untitled': 'Untitled',
  'me.activity.title': 'Your last 12 weeks',
  'me.activity.more': 'more',
  'me.activity.less': 'fewer',
  'me.activity.today': 'today',
  'me.activity.ago': '12 weeks ago',
  'me.activity.how':
    'One square per day: the darker it is, the more puzzles you finished that day.',
  'me.activity.one': '1 day with at least one finished puzzle',
  'me.activity.other': '{{n}} days with at least one finished puzzle',
  'me.fav.none': 'To be discovered',
  'me.fav.noneHint': 'Finish a puzzle to reveal it',
  'me.activity.none': 'No finished puzzle yet: each one you finish darkens the square of its day.',
  'together.cells.eyebrow': 'Cells painted',
  'together.cells.what': 'by everyone, since Monday',
  'together.best.eyebrow': 'Best day',
  'together.best.one': '{{n}} puzzle finished',
  'together.best.other': '{{n}} puzzles finished',
  'together.top.eyebrow': 'Top rated this week',
  'together.top.votes.one': '{{n}} rating',
  'together.top.votes.other': '{{n}} ratings',
  'together.top.done': 'You’ve painted it',
  'together.top.locked': 'In a pack to unlock',
  'together.top.unlock': 'Unlock the pack',
  'me.stat.finished': 'puzzles finished',
  'me.stat.daily': 'daily puzzles',
  'me.stat.styles': 'styles played',
  'me.tally': 'tiers unlocked',
  'me.section.milestones': 'Milestones',
  'me.section.families': 'Alphabets',
  'me.section.categories': 'Categories',
  'me.empty': 'Finish a puzzle to unlock your first badges.',
  'badge.library': 'Library',
  'badge.daily': 'Daily puzzle',
  'badge.share': 'Sharing',
  'badge.family.dots': 'Stippling',
  'badge.family.shapes': 'Shapes',
  'badge.family.hatching': 'Hatching',
  'badge.family.dice': 'Dice',
  'badge.family.ascii': 'ASCII',
  'badge.family.bricks': 'Bricks',
  'badge.family.paint': 'Paint',
  'badge.family.embroidery': 'Cross stitch',
  'badge.goal.library': 'Finish puzzles from the library. Daily puzzles have their own badge.',
  'badge.goal.daily':
    'Finish daily puzzles. No streak needed: a day caught up from the calendar counts too.',
  'badge.goal.share':
    'Share your finished drawings. Each sheet counts once, even if you share it several times.',
  'badge.goal.family': 'Finish puzzles in {{alphabets}}.',
  'badge.goal.category': 'Finish puzzles in the {{category}} category.',
  'badge.unit.puzzles.one': '{{n}} puzzle',
  'badge.unit.puzzles.other': '{{n}} puzzles',
  'badge.unit.shares.one': '{{n}} sheet',
  'badge.unit.shares.other': '{{n}} sheets',
  'badge.next.bronze': 'to Bronze',
  'badge.next.argent': 'to Silver',
  'badge.next.or': 'to Gold',
  'badge.next.platine': 'to Platinum',
  'badge.next.prisme': 'to Prism',
  'badge.reached': '{{tier}} tier reached',
  'badge.earned': 'Earned',
  'badge.locked': 'Locked',

  'celebrate.eyebrow': 'New tier',
  'celebrate.next': 'Next',
  'celebrate.later': 'Later',
  'celebrate.see': 'See my badges',
  'celebrate.skipped.one': '{{tiers}} reached on the way',
  'celebrate.skipped.other': '{{tiers}} reached on the way',
  'celebrate.more.eyebrow': 'And also',
  'celebrate.more.title.one': '{{n}} more tier',
  'celebrate.more.title.other': '{{n}} more tiers',
  'celebrate.more.line': 'You’ll find them all in your badges.',
  'celebrate.welcome.eyebrow': 'New: badges',
  'celebrate.welcome.title': 'Your badges are here',
  'celebrate.welcome.count': 'tiers already earned',
  'celebrate.welcome.line': 'They count everything you’ve coloured so far.',

  // ---------- prefs.ts / la page Réglages (index.html #prefsPage) ----------
  'prefs.lang.label': 'Language',
  'prefs.lang.hint': 'Changes every text in the app',
  'prefs.skin.label': 'Theme',
  'prefs.skin.hint': 'How the screen is dressed — the sheet itself never changes',
  'prefs.sound.label': 'Sound',
  'prefs.sound.hint': 'A note per cell filled',
  'prefs.haptics.label': 'Vibration',
  'prefs.haptics.hint': 'A buzz in the hand on every cell',
  'prefs.motion.label': 'Animations',
  'prefs.motion.hint': 'Cells blooming, particles, shakes',
  'prefs.compass.label': 'Compass',
  'prefs.compass.hint': 'An arrow to the symbol once it scrolls off screen',
  'prefs.push.label': 'Notifications',
  'prefs.push.hint': 'New puzzles, reminders',
  'prefs.inks.label': 'Cell colour',
  'prefs.inks.hint': 'What the sheet fills with',
  'prefs.marks.label': 'Cell marker',
  'prefs.marks.hint': 'How the cells left to fill are marked',
  'prefs.markHues.label': 'Marker colour',
  'prefs.markHues.hint': 'Only affects the marker, not the ink',
  'prefs.username.label': 'Nickname',
  'prefs.username.hint': 'Visible if you ever want to show it',
  'prefs.username.placeholder': 'Pick a nickname',
  'prefs.username.random': 'Suggest a nickname',
  'prefs.username.save': 'Save',
  'prefs.username.saving': 'Saving…',
  'prefs.username.taken': 'That nickname is already taken.',
  'prefs.username.rejected': 'That name can’t be used. Please pick another.',
  'prefs.username.failed': 'Failed — try again once connected.',
  // ---------- the recovery code (prefs.ts) ----------
  'prefs.recovery.label': 'Get my progress back',
  'prefs.recovery.hint': 'A code to keep, to carry on from another phone',
  'prefs.recovery.mine':
    'Write this code down somewhere. It brings your progress back on another device, or after a reinstall.',
  'prefs.recovery.loading': 'One moment…',
  'prefs.recovery.offline': 'Code unavailable — try again once connected.',
  'prefs.recovery.copy': 'Copy',
  'prefs.recovery.copied': 'Copied',
  'prefs.recovery.theirs':
    'Already have a code? Type it here: this device will take over that progress, and what you painted here is kept.',
  'prefs.recovery.placeholder': 'the words separated by dashes',
  'prefs.recovery.use': 'Restore',
  'prefs.recovery.restoring': 'Restoring…',
  'prefs.recovery.unknown': 'That code matches no progress.',
  'prefs.recovery.failed': 'Failed — try again once connected.',
  'prefs.recovery.done': 'Progress recovered.',
  'maker.eyebrow': 'A word from the maker',
  'maker.title': 'Hi, I’m tidou44',
  'maker.who':
    'I’m an independent web developer, and I made Codigrid on my own, for the joy of watching a picture appear cell by cell.',
  'maker.ask':
    'Ads stay light, by choice. If you enjoy the game, sharing or rating it helps a lot: I have plenty of ideas for what comes next.',
  'maker.askWeb':
    'Ads stay light, by choice. If you enjoy the game, sharing it helps a lot: I have plenty of ideas for what comes next.',
  'maker.share': 'Share the game',
  'maker.rate': 'Rate',
  'maker.contact': 'Write to me',
  'maker.shareText':
    'Try Codigrid, a logic colouring game where the grid doesn’t say what it’s hiding. Available on Android only for now.',
  'prefs.ads.label': 'Ad choices',
  'prefs.ads.hint': 'Revisit your consent for ads',
  'prefs.privacy.label': 'Privacy',
  'prefs.privacy.hint': "What stays on the device, what's sent",
  'prefs.wipe.label': 'Delete my data',
  'prefs.wipe.hint': 'The account and every save, here and on the server',
  'prefs.wipe.confirm': 'Delete the account and every save? This cannot be undone.',
  'prefs.wipe.progress': 'Deleting…',
  'prefs.wipe.failed': 'Failed — nothing was deleted. Try again once connected.',

  // ---------- mark.ts (cell marker, game-only — not in shared/) ----------
  'mark.battant.label': 'Thick outline',
  'mark.battant.hint': 'A thick edge, the drawing untouched',
  'mark.hachures.label': 'Hatching',
  'mark.hachures.hint': 'Stripes across the cells',
  'mark.cases.label': 'Cross per cell',
  'mark.cases.hint': 'A cross in the middle of each cell',
  'skin.clair': 'Light',
  'skin.ciel': 'Sky',
  'skin.soir': 'Evening',

  'mark.hue.citron': 'Lime',
  'mark.hue.ambre': 'Amber',
  'mark.hue.rose': 'Pink',
  'mark.tooltip': '{{label}} — {{hint}}',
  'mark.ariaLabel': '{{label}}. {{hint}}',

  // ---------- coach.ts: the three opening gestures ----------
  'coach.boussole': 'The symbol goes on over there. Tap the arrow.',
  'coach.close': 'Hide this tip',
  'ordre.later':
    'Colours go on <b>in order</b>: light to dark, then the whites for the highlights. This one comes later.',
  'coach.hello.title': 'Hi, welcome!',
  'coach.hello.body':
    'I’m the Codigrid fox. Every grid hides a picture: fill in its cells and watch it appear.',
  'coach.hello.next': 'Show me',
  'coach.dlg.title': 'Two ways to colour',
  'coach.dlg.tap': '<b>Tap</b> a highlighted cell — its whole area fills.',
  'coach.dlg.swipe': '<b>Sweep</b> without lifting your finger — every area you cross fills.',
  'coach.dlg.next': 'Next',
  'coach.move.title': 'Getting around the picture',
  'coach.move.drag':
    '<b>Drag</b> the grid to move around, starting from a cell that isn’t highlighted.',
  'coach.move.pinch': '<b>Two fingers</b>: spread them to zoom in, pinch them to zoom out.',
  'coach.dlg.go': 'Let’s go',
  'piece.done.title': 'Well done!',
  'piece.done.body':
    'That was only its gaze: the rest of the picture is waiting. And it’s not the only one.',
  'piece.done.packs': '<b>Hundreds of pictures</b> to download, sorted into packs',
  'piece.done.daily': 'A <b>picture of the day</b>, in the Daily tab',
  'piece.done.create': 'Your <b>own puzzles</b>, made from your photos',
  'piece.done.ask': 'Shall we finish this picture together, or would you like to see the library?',
  'piece.done.finish': 'Finish this picture',
  'piece.done.library': 'See the library',
  'intro.bigDone': 'Your first picture is done! Hundreds more are waiting for you in the library.',
  'piece.bravo': 'Well done!',
  'piece.line': 'That was only its gaze. The rest is waiting for you.',
  'prefs.intro.label': 'Replay the introduction',
  'prefs.intro.hint': 'The first picture, with the fox',
  'prefs.coach.label': 'Show the gestures again',
  'prefs.coach.hint': 'Colouring, zooming, moving around, animated',

  // ---------- update dialog (pwa.ts / index.html #update) ----------
  'update.title': 'Update available',
  'update.body':
    'A new version of Codigrid is ready. It installs by reloading the page — your progress is kept.',
  'update.reload': 'Reload',
  'update.reloading': 'Updating…',

  // ---------- game screen (ui.ts / index.html .app) ----------
  'game.close': 'Close the sheet',
  'game.finished': 'Finished',
  'game.finishedSub': '{{todo}} cells · {{w}}×{{h}} · {{alphabet}}',
  // La ligne « Avec toi, … » de la carte de fin (ui.ts, showPeers).
  'game.peers.sheet': 'Including you, {{n}} players have finished this drawing.',
  'game.peers.daily': "Including you, {{n}} players have painted today's puzzle.",
  'game.peers.first': "You're among the first to finish this drawing!",
  'game.peers.none': 'Nobody had finished it before you!',
  'game.rate': 'Rate this puzzle',
  'game.replay': 'Replay the animation',
  'game.share': 'Share',
  'game.linkCopied': '✓ Link copied',
  'shareSheet.image': 'Image',
  'shareSheet.imageSub': 'The finished drawing',
  'shareSheet.video': 'Video',
  'shareSheet.videoSub': 'The fill, in 10 seconds',
  'shareSheet.imageBusy': 'Preparing the image…',
  'shareSheet.videoBusy': 'Filming your fill…',
  'shareSheet.imageReady': 'Your image is ready!',
  'shareSheet.videoReady': 'Your video is ready!',
  'shareSheet.imageSend': 'Send the image',
  'shareSheet.videoSend': 'Send the video',
  'shareSheet.imageSaved': '✓ Image saved',
  'shareSheet.videoSaved': '✓ Video saved',
  'shareSheet.other': 'Choose something else',
  'shareSheet.failed': "The file couldn't be prepared. Try again in a moment.",
  'shareSheet.msg.mineVideo':
    'My photo becomes a drawing, cell by cell. Made in Codigrid, free on Android:\n{{url}}',
  'shareSheet.msg.mineImage':
    'My photo, turned into a colouring page in Codigrid. Free on Android:\n{{url}}',
  'shareSheet.msg.video':
    'Watch this drawing appear cell by cell. Codigrid, free on Android:\n{{url}}',
  'shareSheet.msg.image': 'Coloured cell by cell in Codigrid. Free on Android:\n{{url}}',
  'game.shareText':
    "I just finished a sheet on Codigrid — a logic colouring game where the grid doesn't say what it's hiding.",

  // ---------- splash.ts ----------
  // Kept close in length to the French original — see the warning next to L1/L2 in splash.ts:
  // fit() sizes the splash type from these two lines' exact character count.
  'splash.line1': 'One cell at a time,',
  'splash.line2': 'the picture appears',

  // ---------- report.ts (a start-up crash) ----------
  'crash.boot': 'The game could not start.',

  // ---------- share.ts (injected into p.html on load) ----------
  'share.title': '"{{title}}", finished',
  'share.meta': '{{w}} × {{h}} · {{alphabet}}',
  'share.docTitle': '{{title}} — Codigrid',

  // ---------- p.html (the static skeleton of the share page) ----------
  'share.page.metaDescription':
    'Codigrid, a logic colouring game: a grid of coded symbols, and the picture that appears under your fingers.',
  'share.page.docTitleDefault': 'A finished sheet — Codigrid',
  'share.page.title': 'A finished sheet',
  'share.page.pitch':
    "Codigrid is a logic colouring game. Every cell carries a coded symbol, you fill them one symbol at a time, and the picture appears under your hand. <em>The grid never says what it's hiding.</em>",
  'share.page.play': 'Play now',
  'share.page.store': 'Get it on Google Play',
  'share.page.fine': 'Free, no account. A new sheet every day.',
  'share.page.fail': 'This drawing is no longer available.',
  'share.page.again': '↻ Replay the fill-in',
};

export default en;
